import React, { Component, Dispatch } from 'react';
import { SofaPanel, SofaButton } from '@sofology/sofologycomponents';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import './DataProtectionNoticePage.scss';

import { routes } from 'routes';
import { generateDispatchToPropsMap, paymentHelper, GtmCheckoutSteps, ExponeaCheckoutSteps, scrollHelper } from 'helpers';
import { AppState } from 'store';
import { PaymentTypes } from 'models/Payment';
import DataProtectionNoticeForm, { DataProtectionNoticeFormType, CONFIRMATION_REQUIRED_ERROR_MESSAGE } from './DataProtectionNoticeForm/DataProtectionNoticeForm';
import { initCheckoutPage, InitCheckoutPageFunc, preprocessRedirectToNextStep, PreprocessRedirectToNextStepFunc } from 'store/checkout/actions';

export interface DataProtectionNoticeFormData {
    confirmFinancialAssociatesAgreement: boolean;
}

interface OwnState {
    hasError: boolean;
}

interface StateProps {
    paymentType: PaymentTypes;
}

interface DispatchProps {
    initCheckoutPage: InitCheckoutPageFunc;
    preprocessRedirectToNextStep: PreprocessRedirectToNextStepFunc;
}

class DataProtectionNoticePage extends Component<StateProps & DispatchProps & RouteComponentProps, OwnState> {
    private dataProtectionNoticeForm = React.createRef<DataProtectionNoticeFormType>();

    constructor(props: StateProps & DispatchProps & RouteComponentProps) {
        super(props);

        this.state = {
            hasError: false
        };
    }

    componentDidMount() {
        this.props.initCheckoutPage();
    }

    componentDidUpdate(): void {
        /** redirect if payment type was changed on the basket page */
        if (paymentHelper.isCardRelatedPaymentType(this.props.paymentType)) {
            this.props.history.push(routes.payment.path);
        }
    }

    handleContinueClick = async () => {
        let invalidControls = this.dataProtectionNoticeForm.current ? await this.dataProtectionNoticeForm.current.validate() : [];

        if (invalidControls.length > 0) {
            this.setState({ hasError: true });
            let firstInvalidControl = invalidControls.min(c => c.index);
            if (firstInvalidControl.ref?.current) {
                scrollHelper.scrollTo(firstInvalidControl.ref?.current.element);
            }
        } else {
            this.props.preprocessRedirectToNextStep(
                () => this.props.history.push(routes.financeApplication.path),
                GtmCheckoutSteps.FinanceFormStepOne,
                ExponeaCheckoutSteps.DataProtectionNotice
            );

        }
    };

    render(): JSX.Element {
        return (
            <div className='data-protection-notice'>
                {this.renderDataProtectionNotice()}
                {this.renderConfirmationPanel()}
                <div className='data-protection-notice__continue-button'>
                    <SofaButton
                        text='Continue'
                        variant='primary'
                        onClick={this.handleContinueClick}
                    />
                </div>
            </div>
        );
    }
    renderDataProtectionNotice(): JSX.Element {
        return (
            <SofaPanel>
                <div className='data-protection-notice__title'>How we use your information to apply for credit</div>
                <div className='data-protection-notice__info-title'>Data Protection & Credit Brokerage Notice</div>
                <div className='data-protection-notice__info'>
                    Before completing your application, please read the statement below regarding how your information will be used and shared.
                </div>
                <div className='data-protection-notice__info-title'>Our Role as a Credit Broker</div>
                <div className='data-protection-notice__info'>
                    Sofology act as a credit broker, not a lender. We pass your application details to the lenders on our panel to assess your eligibility for finance. Lenders will use credit scoring systems and check records with Credit Reference Agencies and Fraud Prevention Agencies.
                </div>
                <div className='data-protection-notice__info-title'>Your application</div>
                <div className='data-protection-notice__info'>
                    We will initially submit your application to one or more of the lenders on our panel offering Interest-Free Credit (IFC). If your application for IFC is unsuccessful, we will automatically pass your details to an alternative lender on our panel to see if they can offer you Interest-Bearing Credit (IBC).
                </div>
                <div className='data-protection-notice__info'>
                    Please note: This offer will include interest fees and an APR personal to your circumstances. If this applies to you it will be clearly communicated and you are under no obligation to accept any alternative offer.
                </div>
                <div className='data-protection-notice__info-title'>Credit Searches & Your Record</div>
                <div className='data-protection-notice__info'>
                    All lenders on our panel perform soft credit searches to check your eligibility, which will not affect your credit score. If you accept a finance offer and proceed, the chosen lender will perform a formal hard credit search, which will be recorded on your credit file. If you provide details about a partner, please ensure you have their consent to do so.
                </div>
                <div className='data-protection-notice__info'>
                    The credit can only be used for the purchase of the goods or services identified in the credit agreement. You should consider carefully your ability to make the repayments. If you do not make the repayments that are due under this agreement then your credit rating may be adversely affected and could make obtaining credit more difficult in the future. It may also result in legal action being taken to recover the debt. Details of late payment charges are set out in the Pre-Contract Credit Information provided to you by the lender before you sign the documentation.
                </div>
                <div className='data-protection-notice__info-title'>Your Rights & Contact</div>
                <div className='data-protection-notice__info'>
                    You have the right to withdraw from a credit agreement which starts the day after you sign the agreement and lasts for 14 days. Withdrawing from a credit agreement does not cancel your order for goods; an alternative payment method will be required. Full details of payment terms are set out in the Pre-Contract Credit Information (SECCI) provided by the lender before you sign.
                </div>
                <div className='data-protection-notice__info'>
                    For full details of our panel lenders and how your data is processed, please view our Privacy Policy. If you have questions, call us free on 08001404040.
                </div>
            </SofaPanel>
        );
    }

    renderConfirmationPanel(): JSX.Element {
        const hasError = this.state.hasError;

        return (
            <div className='validation-scroll-container'>
                <SofaPanel>
                    <div className='data-protection-notice__confirmation-title'>Please make sure you've read the above and <span>confirm below</span></div>
                    <DataProtectionNoticeForm ref={this.dataProtectionNoticeForm} />
                    {hasError && <div className='data-protection-notice__confirmation-error'>{CONFIRMATION_REQUIRED_ERROR_MESSAGE}</div>}
                </SofaPanel>
            </div>
        );
    }
}

function mapStateToProps(state: AppState): StateProps {
    return {
        paymentType: state.paymentState.paymentDetails.paymentType
    };
}


function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [initCheckoutPage, preprocessRedirectToNextStep]);
}

export default connect(mapStateToProps, mapDispatchToProps)(DataProtectionNoticePage);
